import { Space, Tag } from 'antd';
import type { ComplianceReadiness, ComplianceSummary } from '@ubus/shared';
import { useTranslation } from 'react-i18next';

type Props = {
  summary: ComplianceSummary;
  activeFilter?: ComplianceReadiness;
  /** Selecting a chip scopes the list to that readiness (+ active-only, handled by parent). */
  onSelect: (readiness: ComplianceReadiness | undefined) => void;
};

const CHIPS: { key: keyof ComplianceSummary; readiness: ComplianceReadiness; color: string }[] = [
  { key: 'expired', readiness: 'expired', color: 'error' },
  { key: 'dueSoon', readiness: 'due_soon', color: 'warning' },
  { key: 'incomplete', readiness: 'incomplete', color: 'default' },
  { key: 'ready', readiness: 'ready', color: 'success' },
];

export default function ComplianceSummaryBar({ summary, activeFilter, onSelect }: Props) {
  const { t } = useTranslation();

  return (
    <Space wrap size={[8, 8]} align="center">
      <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
        {t('compliance.summaryActiveOnly')}
      </span>
      {CHIPS.map(({ key, readiness, color }) => {
        const count = summary[key];
        const selected = activeFilter === readiness;
        return (
          <button
            key={readiness}
            type="button"
            aria-pressed={selected}
            aria-label={`${t(`compliance.${readiness}`)} ${count}`}
            onClick={() => onSelect(selected ? undefined : readiness)}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              borderRadius: 4,
              outlineOffset: 2,
            }}
          >
            <Tag
              color={color}
              style={{
                marginInlineEnd: 0,
                outline: selected ? '2px solid rgba(0,0,0,0.35)' : undefined,
                outlineOffset: 1,
              }}
            >
              {t(`compliance.${readiness}`)} · {count}
            </Tag>
          </button>
        );
      })}
    </Space>
  );
}
